<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex flex-wrap items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">Prompt Marketing 2025</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">20 prompt AI Marketing/SEO "cắm là chạy" cho năm 2025</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>—</span>
          <span class="mx-2">•</span>
          <span>6 phút đọc</span>
          <span class="mx-2">•</span>
          <span>Marketing & SEO</span>
        </div>
        <div class="h-48 sm:h-56 lg:h-64 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg flex items-center justify-center">
          <svg class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="prose prose-lg max-w-none">
          <h2>AI Marketing/SEO là gì và tại sao quan trọng?</h2>
          <p>AI Marketing sử dụng trí tuệ nhân tạo để tối ưu hóa chiến lược marketing, tăng hiệu quả và giảm chi phí. Với 20 prompt được tinh chỉnh, bạn có thể tạo ra content chất lượng cao chỉ trong vài phút.</p>
          
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="text-xl font-semibold mb-4 text-blue-800">🚀 Lợi ích của AI Marketing</h3>
            <ul class="space-y-2 text-blue-700">
              <li>• <strong>Tiết kiệm thời gian:</strong> Tạo content nhanh gấp 10 lần</li>
              <li>• <strong>Tăng hiệu quả:</strong> Content được tối ưu cho SEO</li>
              <li>• <strong>Giảm chi phí:</strong> Không cần thuê copywriter</li>
              <li>• <strong>Consistency:</strong> Tone và style nhất quán</li>
              <li>• <strong>Scalability:</strong> Tạo hàng trăm content cùng lúc</li>
            </ul>
          </div>

          <h2>1. Content Marketing - Tạo nội dung chất lượng</h2>
          <div class="bg-green-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-green-800">📝 Blog Post & Articles</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-green-600 mb-2">Blog Post SEO</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Viết bài blog 2000 từ về "[chủ đề]", tối ưu SEO với:<br/>
                  - Title hấp dẫn, dưới 60 ký tự<br/>
                  - Meta description 150-160 ký tự<br/>
                  - 3-5 heading H2, H3 có từ khóa<br/>
                  - 5-10 từ khóa LSI tự nhiên<br/>
                  - Call-to-action mạnh mẽ<br/>
                  - Internal links 3-5 link<br/>
                  Tone: [chuyên nghiệp/thân thiện/authority]</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-blue-600 mb-2">Long-form Content</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo hướng dẫn chi tiết 3000 từ về "[chủ đề]":<br/>
                  - Outline 10 phần chính<br/>
                  - Mỗi phần 300-400 từ<br/>
                  - Case study thực tế<br/>
                  - Screenshots và examples<br/>
                  - FAQ section 5 câu hỏi<br/>
                  - Conclusion với next steps</code>
                </div>
              </div>
            </div>
          </div>

          <h3>Social Media Content:</h3>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-pink-600 mb-2">Facebook/Instagram Posts</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo 5 post Facebook/Instagram cho "[sản phẩm/dịch vụ]":<br/>
                  - Hook mạnh trong 3 giây đầu<br/>
                  - Storytelling cá nhân<br/>
                  - 3-5 hashtag trending<br/>
                  - CTA rõ ràng<br/>
                  - Tone [vui vẻ/chuyên nghiệp/inspirational]<br/>
                  - Format: text + emoji + hashtag</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-blue-600 mb-2">LinkedIn Professional</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Viết LinkedIn post cho "[chủ đề chuyên môn]":<br/>
                  - Hook với statistic/insight<br/>
                  - 3 key points với examples<br/>
                  - Personal experience<br/>
                  - Question để tăng engagement<br/>
                  - Professional tone<br/>
                  - 1300-2000 ký tự</code>
                </div>
              </div>
            </div>
          </div>

          <h2>2. SEO Optimization - Tối ưu tìm kiếm</h2>
          <div class="bg-yellow-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-yellow-800">🔍 Keyword Research & Strategy</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-yellow-600 mb-2">Keyword Research</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tìm 20 từ khóa cho "[ngành/lĩnh vực]":<br/>
                  - Primary keywords (1-2 từ)<br/>
                  - Long-tail keywords (3-5 từ)<br/>
                  - LSI keywords liên quan<br/>
                  - Local keywords (nếu có)<br/>
                  - Competitor keywords<br/>
                  - Search volume ước tính<br/>
                  - Difficulty level (1-10)</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-orange-600 mb-2">Content Cluster</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo content cluster cho "[từ khóa chính]":<br/>
                  - 1 pillar page (3000+ từ)<br/>
                  - 5-7 supporting articles (1500 từ)<br/>
                  - Internal linking strategy<br/>
                  - Topic authority mapping<br/>
                  - User intent coverage<br/>
                  - Content calendar 3 tháng</code>
                </div>
              </div>
            </div>
          </div>

          <h3>Technical SEO:</h3>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-purple-600 mb-2">Meta Tags</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo meta tags cho "[trang web]":<br/>
                  - Title tag (50-60 ký tự)<br/>
                  - Meta description (150-160 ký tự)<br/>
                  - H1 tag (20-30 ký tự)<br/>
                  - Alt text cho images<br/>
                  - Schema markup suggestions<br/>
                  - Focus keyword: "[từ khóa]"</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-indigo-600 mb-2">Sitemap & Structure</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tối ưu cấu trúc website cho "[domain]":<br/>
                  - URL structure (3-4 levels max)<br/>
                  - Breadcrumb navigation<br/>
                  - XML sitemap priority<br/>
                  - Internal linking hierarchy<br/>
                  - Page speed optimization<br/>
                  - Mobile-first indexing</code>
                </div>
              </div>
            </div>
          </div>

          <h2>3. Email Marketing - Tăng conversion</h2>
          <div class="bg-red-50 p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-red-800">📧 Email Campaigns</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-red-600 mb-2">Subject Lines</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo 10 subject line cho "[campaign]":<br/>
                  - Urgency/FOMO (2 lines)<br/>
                  - Personalization (2 lines)<br/>
                  - Benefit-focused (2 lines)<br/>
                  - Curiosity gap (2 lines)<br/>
                  - Social proof (2 lines)<br/>
                  - A/B test variants<br/>
                  - Dưới 50 ký tự</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-pink-600 mb-2">Email Sequences</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Viết email sequence 7 ngày cho "[sản phẩm]":<br/>
                  - Day 1: Welcome + value<br/>
                  - Day 3: Social proof + testimonial<br/>
                  - Day 5: Educational content<br/>
                  - Day 7: Soft offer + CTA<br/>
                  - Personal tone, 200-300 từ<br/>
                  - Mobile-optimized format</code>
                </div>
              </div>
            </div>
          </div>

          <h2>4. Paid Advertising - Tối ưu quảng cáo</h2>
          <div class="bg-purple-50 p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-purple-800">💰 Facebook/Google Ads</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-purple-600 mb-2">Ad Copy</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo Facebook ad copy cho "[sản phẩm]":<br/>
                  - Headline (25 ký tự)<br/>
                  - Primary text (125 ký tự)<br/>
                  - Description (30 ký tự)<br/>
                  - 3 variants A/B/C<br/>
                  - Emotional triggers<br/>
                  - Clear value proposition<br/>
                  - Strong CTA button</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-indigo-600 mb-2">Google Ads</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Viết Google Search ads cho "[dịch vụ]":<br/>
                  - 3 headlines (30 ký tự)<br/>
                  - 2 descriptions (90 ký tự)<br/>
                  - Keywords: [từ khóa chính]<br/>
                  - Ad extensions ideas<br/>
                  - Landing page match<br/>
                  - Quality score optimization</code>
                </div>
              </div>
            </div>
          </div>

          <h2>5. Video & Visual Content</h2>
          <div class="bg-teal-50 p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-teal-800">🎥 Video Scripts</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-teal-600 mb-2">YouTube Scripts</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Viết YouTube script 10 phút về "[chủ đề]":<br/>
                  - Hook mạnh (0-15 giây)<br/>
                  - Problem/solution structure<br/>
                  - 3-5 key points<br/>
                  - Call-to-action rõ ràng<br/>
                  - Timestamps cho chapters<br/>
                  - SEO-optimized title/description</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-cyan-600 mb-2">TikTok/Reels Scripts</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo TikTok script 30 giây cho "[trend]":<br/>
                  - Trending audio/sound<br/>
                  - Quick hook (3 giây)<br/>
                  - Visual transitions<br/>
                  - Trending hashtags<br/>
                  - Challenge/trend participation<br/>
                  - Viral potential elements</code>
                </div>
              </div>
            </div>
          </div>

          <h2>6. E-commerce & Sales</h2>
          <div class="bg-orange-50 p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-orange-800">🛒 Product Descriptions</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-orange-600 mb-2">Product Copy</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Viết product description cho "[sản phẩm]":<br/>
                  - Compelling headline<br/>
                  - 3-5 key benefits<br/>
                  - Technical specifications<br/>
                  - Social proof elements<br/>
                  - Urgency/scarcity tactics<br/>
                  - Clear pricing/value<br/>
                  - Strong CTA button</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-amber-600 mb-2">Sales Pages</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo sales page cho "[dịch vụ]":<br/>
                  - Problem/agitation/solution<br/>
                  - 3-5 testimonials<br/>
                  - FAQ section<br/>
                  - Risk reversal/guarantee<br/>
                  - Multiple CTAs<br/>
                  - Mobile-optimized layout<br/>
                  - Conversion tracking setup</code>
                </div>
              </div>
            </div>
          </div>

          <h2>7. Analytics & Optimization</h2>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-gray-800">📊 Performance Analysis</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-gray-600 mb-2">Campaign Analysis</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Phân tích campaign "[tên campaign]":<br/>
                  - CTR, CPC, CPM metrics<br/>
                  - Conversion rate analysis<br/>
                  - Audience insights<br/>
                  - Creative performance<br/>
                  - Budget optimization<br/>
                  - A/B test results<br/>
                  - Recommendations for improvement</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-slate-600 mb-2">Content Performance</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Đánh giá content performance:<br/>
                  - Organic traffic growth<br/>
                  - Keyword ranking changes<br/>
                  - Engagement metrics<br/>
                  - Backlink acquisition<br/>
                  - Social shares/virality<br/>
                  - ROI calculation<br/>
                  - Next quarter strategy</code>
                </div>
              </div>
            </div>
          </div>

          <h2>8. Advanced Strategies</h2>
          <div class="bg-indigo-50 p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-indigo-800">🎯 Advanced Marketing</h3>
            <div class="space-y-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-indigo-600 mb-2">Personalization</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Tạo personalized content cho "[audience segment]":<br/>
                  - Demographic targeting<br/>
                  - Behavioral triggers<br/>
                  - Purchase history analysis<br/>
                  - Dynamic content blocks<br/>
                  - Personalized recommendations<br/>
                  - Email automation triggers<br/>
                  - Cross-channel consistency</code>
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-violet-600 mb-2">Omnichannel Strategy</h4>
                <div class="bg-gray-900 text-green-400 p-2 sm:p-3 rounded text-xs sm:text-sm overflow-x-auto">
                  <code>Phát triển omnichannel campaign:<br/>
                  - Social media integration<br/>
                  - Email + SMS coordination<br/>
                  - Website + app consistency<br/>
                  - Offline + online touchpoints<br/>
                  - Customer journey mapping<br/>
                  - Attribution modeling<br/>
                  - Unified customer experience</code>
                </div>
              </div>
            </div>
          </div>

          <h2>9. Best Practices & Tips</h2>
          <div class="bg-green-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-green-800">💡 Pro Tips</h3>
            <ul class="space-y-2 text-green-700">
              <li>• <strong>Test everything:</strong> A/B test mọi element</li>
              <li>• <strong>Data-driven:</strong> Dựa vào data, không đoán</li>
              <li>• <strong>Consistency:</strong> Brand voice nhất quán</li>
              <li>• <strong>Mobile-first:</strong> Tối ưu mobile trước</li>
              <li>• <strong>User intent:</strong> Hiểu intent của user</li>
              <li>• <strong>Value first:</strong> Cung cấp value trước khi sell</li>
              <li>• <strong>Track everything:</strong> Setup tracking đầy đủ</li>
            </ul>
          </div>

          <div class="bg-blue-50 p-6 rounded-lg mt-8">
            <h3 class="font-semibold mb-3 text-blue-800">🚀 Kết quả mong đợi</h3>
            <p class="text-sm text-blue-700">Với 20 prompt này, bạn có thể tạo ra hàng trăm content chất lượng cao, tối ưu SEO và tăng conversion rate. Chỉ cần thay thế [placeholder] bằng thông tin thực tế và chạy prompt.</p>
          </div>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'
const title = "20 prompt AI Marketing/SEO 'cắm là chạy' cho năm 2025"
const desc  = '20 prompt AI Marketing/SEO được tinh chỉnh để tạo content chất lượng cao, tối ưu SEO và tăng conversion rate. Sử dụng AI để tăng hiệu quả marketing'
const url   = 'https://webservices.vn/blog/prompt-marketing-seo-2025'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
